import React, { useState } from 'react'
import { useQuery, useMutation } from '@apollo/client'
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  IconButton,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Switch,
  FormControlLabel,
  Button,
} from '@mui/material'
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
} from '@mui/icons-material'
import { toast } from 'react-toastify'
import { GET_ACTIVITIES, CREATE_ACTIVITY, UPDATE_ACTIVITY, DELETE_ACTIVITY } from '../../api/activity'
import { GET_DEPARTMENTS } from '../../api/department'
import HistoryIcon from '@mui/icons-material/History'
import Loader from '../Loader'
import PageHeader from '../Common/PageHeader'

interface Activity {
  id: number
  title: string
  description: string | null
  active: boolean
  department_id: string
  department: {
    id: string
    name: string
  }
}

interface Department {
  id: string
  name: string
}

const Activities: React.FC = () => {
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editingActivity, setEditingActivity] = useState<Partial<Activity>>({})
  const [isAdding, setIsAdding] = useState(false)
  const [newActivity, setNewActivity] = useState<Partial<Activity>>({
    title: '',
    description: '',
    active: true,
    department_id: '',
  })

  const { data: activitiesData, loading: activitiesLoading, refetch: refetchActivities } = useQuery(GET_ACTIVITIES)
  const { data: departmentsData, loading: departmentsLoading } = useQuery(GET_DEPARTMENTS)

  const [createActivity] = useMutation(CREATE_ACTIVITY, {
    onCompleted: () => {
      toast.success('Activity created successfully')
      setIsAdding(false)
      setNewActivity({ title: '', description: '', active: true, department_id: '' })
      refetchActivities()
    },
    onError: (error) => toast.error(`Error: ${error.message}`),
  })

  const [updateActivity] = useMutation(UPDATE_ACTIVITY, {
    onCompleted: () => {
      toast.success('Activity updated successfully')
      setEditingId(null)
      refetchActivities()
    },
    onError: (error) => toast.error(`Error: ${error.message}`),
  })

  const [deleteActivity] = useMutation(DELETE_ACTIVITY, {
    onCompleted: () => {
      toast.success('Activity deleted successfully')
      refetchActivities()
    },
    onError: (error) => toast.error(`Error: ${error.message}`),
  })

  const activities: Activity[] = activitiesData?.activities || []
  const departments: Department[] = departmentsData?.departments || []

  const handleEdit = (activity: Activity) => {
    setEditingId(activity.id)
    setEditingActivity({ ...activity })
  }

  const handleSaveEdit = async () => {
    if (!editingId) return
    try {
      await updateActivity({
        variables: {
          id: editingId,
          input: {
            title: editingActivity.title,
            description: editingActivity.description,
            active: editingActivity.active,
            department_id: editingActivity.department_id,
          },
        },
      })
    } catch (err) {
      console.error(err)
    }
  }

  const handleCreate = async () => {
    if (!newActivity.title || !newActivity.department_id) {
      toast.error('Title and Department are required')
      return
    }
    try {
      await createActivity({
        variables: {
          input: {
            title: newActivity.title,
            description: newActivity.description,
            active: newActivity.active,
            department_id: newActivity.department_id,
          },
        },
      })
    } catch (err) {
      console.error(err)
    }
  }

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this activity?')) {
      try {
        await deleteActivity({ variables: { id } })
      } catch (err) {
        console.error(err)
      }
    }
  }

  if (activitiesLoading || departmentsLoading) {
    return <Loader minHeight={400} />
  }

  return (
    <Box p={3}>
      <PageHeader title="Activities Management" icon={HistoryIcon} />

      <Box mb={3} p={2} component={Paper} variant="outlined">
        <Typography variant="h6" gutterBottom>
          Add New Activity
        </Typography>
        <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
          <TextField
            label="Title"
            size="small"
            value={newActivity.title}
            onChange={(e) => setNewActivity({ ...newActivity, title: e.target.value })}
            sx={{ flex: 1, minWidth: '200px' }}
          />
          <TextField
            label="Description"
            size="small"
            value={newActivity.description}
            onChange={(e) => setNewActivity({ ...newActivity, description: e.target.value })}
            sx={{ flex: 2, minWidth: '300px' }}
          />
          <FormControl size="small" sx={{ minWidth: '200px' }}>
            <Select
              displayEmpty
              value={newActivity.department_id}
              onChange={(e) => setNewActivity({ ...newActivity, department_id: e.target.value as string })}
            >
              <MenuItem value="" disabled>Select Department</MenuItem>
              {departments.map((dept) => (
                <MenuItem key={dept.id} value={dept.id}>{dept.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Switch
                checked={newActivity.active}
                onChange={(e) => setNewActivity({ ...newActivity, active: e.target.checked })}
              />
            }
            label="Active"
          />
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleCreate}
          >
            Add
          </Button>
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Title</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Department</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {activities.map((activity) => (
              <TableRow key={activity.id}>
                {editingId === activity.id ? (
                  <>
                    <TableCell>
                      <TextField
                        size="small"
                        fullWidth
                        value={editingActivity.title}
                        onChange={(e) => setEditingActivity({ ...editingActivity, title: e.target.value })}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        fullWidth
                        value={editingActivity.description || ''}
                        onChange={(e) => setEditingActivity({ ...editingActivity, description: e.target.value })}
                      />
                    </TableCell>
                    <TableCell>
                      <FormControl size="small" fullWidth>
                        <Select
                          value={editingActivity.department_id}
                          onChange={(e) => setEditingActivity({ ...editingActivity, department_id: e.target.value as string })}
                        >
                          {departments.map((dept) => (
                            <MenuItem key={dept.id} value={dept.id}>{dept.name}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={editingActivity.active}
                        onChange={(e) => setEditingActivity({ ...editingActivity, active: e.target.checked })}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton onClick={handleSaveEdit} color="primary">
                        <SaveIcon />
                      </IconButton>
                      <IconButton onClick={() => setEditingId(null)} color="secondary">
                        <CancelIcon />
                      </IconButton>
                    </TableCell>
                  </>
                ) : (
                  <>
                    <TableCell>{activity.title}</TableCell>
                    <TableCell>{activity.description || '-'}</TableCell>
                    <TableCell>{activity.department?.name}</TableCell>
                    <TableCell>
                      <Typography color={activity.active ? 'success.main' : 'error.main'} variant="body2" fontWeight="bold">
                        {activity.active ? 'ACTIVE' : 'INACTIVE'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton onClick={() => handleEdit(activity)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton onClick={() => handleDelete(activity.id)} color="error">
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </>
                )}
              </TableRow>
            ))}
            {activities.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  No activities found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}

export default Activities
